import { captureException } from '@sentry/nextjs';
import type { AdItem } from '@sbt-web/network/types';
import { GENERIC_SEARCH_FILTERS_STORAGE_KEY } from '@/client/bridges/constants';
import { getStore } from '@/reducers/index';
import { PULSE_SCHEMA_VERSION } from '@/tools/tracking/constants';
import { getAvailableFilters } from '@/tools/tracking/getAvailableFiltersHelper';
import {
  transformAds,
  transformCategory,
  transformFilters,
  transformSearch,
} from '@/tools/tracking/transformer';
import {
  detectAdBlocker,
  getDataLayer,
  getOrCreatePulse,
} from '@/tools/tracking/utils';

async function sendEvent(geoboostEventOverride?: {
  eventType: 'View' | 'Watch';
  objectType: string;
  objectId: string;
  items: Array<AdItem>;
  rank: number;
}): Promise<void> {
  const rawData = getDataLayer();

  const { search: searchState, filtersConfig: filtersConfigState } = getStore(
    undefined,
    false
  ).getState();

  const availableFilters = getAvailableFilters(
    searchState,
    filtersConfigState,
    rawData.search,
    rawData.ads.length
  );

  const { id: searchId, radiusSearch, filterOrigin } = searchState;

  if (radiusSearch != null) {
    rawData.search['geo'] = null;
    rawData.search['radiusSearch'] = radiusSearch;
  }

  const filters = transformFilters(rawData.search, rawData.ads);

  if (Object.values(rawData.search.filters).some((f) => !f.name)) {
    captureException(new Error('sendEvent found a missing filter name'), {
      level: 'error',
      extra: {
        filters: rawData.search.filters,
        isGeoboost: !!geoboostEventOverride,
        geoboostEventOverride,
      },
    });
  }

  const searchMetadata = transformSearch(
    rawData.search,
    rawData.ads,
    searchId,
    filterOrigin,
    availableFilters
  );
  if (searchMetadata && geoboostEventOverride) {
    searchMetadata.rank = geoboostEventOverride.rank;
  }

  localStorage.setItem(
    GENERIC_SEARCH_FILTERS_STORAGE_KEY,
    JSON.stringify(searchMetadata?.genericSearchFilters ?? [])
  );

  const data = {
    type: geoboostEventOverride?.eventType ?? 'View',
    object: {
      type: geoboostEventOverride?.objectType ?? 'Listing',
      numItems: parseInt(rawData.ads.totalResults, 10),
      category: transformCategory(rawData.search.category),
      items: transformAds(
        geoboostEventOverride?.items ?? rawData.ads,
        rawData.page
      ),
      ...filters,
      ...(geoboostEventOverride?.objectId
        ? { '@id': geoboostEventOverride?.objectId }
        : {}),
    },
    ...(searchMetadata != null ? { search: searchMetadata } : {}),
    page: { pageType: 'listing' },
    adBlock: await detectAdBlocker(),
    schema: PULSE_SCHEMA_VERSION,
  };
  getOrCreatePulse()?.queueEvent(data);
}

export { sendEvent };
